import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  useInterval,
  Link as ChakraLink
} from '@chakra-ui/react';
import { ModalType } from 'components/modals/types';
import { useRewardPrograms } from 'store/zustand/useRewardPrograms';
import { useModal } from 'store/modals';
import { Crucible, ProgramToken } from 'types';
import { fetchBlockTimestamp } from 'api/blockchain/blockTimestamp';
import { useEffect } from 'react';
import { useWeb3React } from 'hooks/web3';
import { useSubscriptions } from 'hooks/query/useSubscriptions';
import { useStakingTokenBalance } from 'hooks/query/useStakingTokenBalance';
import { useRewardProgramState } from 'hooks/query/useRewardProgramState';
import { usePrograms } from 'hooks/query/usePrograms';
import { useTransactions } from 'store/transactions/useTransactions';
import { PowerSwitchStatus } from 'api/blockchain/rewardProgram';
import { TxnStatus, TxnType } from 'store/transactions/types';
import { useConfig } from 'store/config';
import { useSelectedAssets } from 'store/selectedAssets';
import { useWalletAssets } from 'hooks/query/useWalletAssets';
import formatNumber from 'utils/formatNumber';
import dayjs from 'dayjs';

type Props = {
  crucible: Crucible;
  stakingToken: ProgramToken;
  setTabIndex: (index: number) => void;
  setTransferTabIndex: (index: number) => void;
};

const RewardProgramActions: FC<Props> = ({
  crucible,
  stakingToken,
  setTabIndex,
  setTransferTabIndex
}) => {
  const { chainId = 1 } = useWeb3React();
  const { openModal } = useModal();
  const { data: extendedPrograms } = usePrograms();
  const { currentRewardProgram } = useRewardPrograms();
  const { data: rewardProgramState, isLoading: rewardProgramStateLoading } =
    useRewardProgramState(currentRewardProgram);
  const { isLoading: subscriptionsLoading } = useSubscriptions();
  const [blockTimestamp, setBlockTimestamp] = useState<number>(0);
  const [blockTimestampLoading, setBlockTimestampLoading] = useState(true);
  const [currentTimestamp, setCurrentTimestamp] = useState(blockTimestamp);
  const { transactions } = useTransactions();
  const { wrappableTokens, crucibleFactoryAddress } = useConfig(chainId).config;
  const { data: walletAssets } = useWalletAssets();
  const { setSelectedWalletAsset } = useSelectedAssets();

  useInterval(
    () => !!blockTimestamp && setCurrentTimestamp(currentTimestamp + 1),
    1000
  );

  useEffect(() => {
    setBlockTimestampLoading(true);
    fetchBlockTimestamp(chainId)
      .then((ts) => {
        setCurrentTimestamp(ts);
        setBlockTimestamp(ts);
        setBlockTimestampLoading(false);
      })
      .catch((e) => {
        setBlockTimestampLoading(false);
        console.log(e);
      });
  }, [chainId]);

  const { data: stakingTokenData } = useStakingTokenBalance(
    stakingToken.address,
    currentRewardProgram.address,
    crucible.id
  );

  const isExiting = useMemo(() => {
    return !!transactions.find(
      (txn) =>
        txn.type === TxnType.rageQuit &&
        txn.status === TxnStatus.PendingOnChain &&
        txn.crucibleAddress === crucible.id
    );
  }, [transactions, crucible.id]);

  const extendedProgram = useMemo(() => {
    if (extendedPrograms) {
      return extendedPrograms.find(
        (extendedProgram) =>
          extendedProgram.rewardProgramAddress === currentRewardProgram.address
      );
    }
  }, [extendedPrograms, currentRewardProgram.address]);

  const rewardProgramExpired = useMemo(() => {
    // Close entry for programs with shutdown/offline status, or blocked program/maintainer
    // Also close entry for programs with the incorrect vault factory

    if (
      extendedProgram &&
      (extendedProgram.rewardProgram.isProgramBlocked ||
        extendedProgram.rewardProgram.isMaintainerBlacklisted ||
        rewardProgramState === PowerSwitchStatus.OFFLINE ||
        rewardProgramState === PowerSwitchStatus.SHUTDOWN ||
        !!!extendedProgram.rewardProgram.vaultFactories.find(
          (vf) =>
            vf.id.split('-')[1].toLowerCase() ===
            crucibleFactoryAddress.toLowerCase()
        ))
    ) {
      return true;
    }

    return currentTimestamp && extendedProgram
      ? dayjs
          .unix(extendedProgram.rewardProgram.expiryTimestamp as number)
          .isBefore(dayjs.unix(currentTimestamp))
      : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimestamp, extendedProgram]);

  const pendingLaunch = useMemo(() => {
    return !!(
      currentRewardProgram.startTime &&
      currentRewardProgram.startTime > currentTimestamp
    );
  }, [currentTimestamp, currentRewardProgram.startTime]);

  const getCountDown = () => {
    const remaining = currentRewardProgram.startTime! - currentTimestamp;
    const seconds = Math.floor(remaining % 60);
    const minutes = Math.floor((remaining / 60) % 60);
    const hours = Math.floor((remaining / (60 * 60)) % 24);
    const days = Math.floor(remaining / (60 * 60 * 24));
    return `Starts in: 
    ${days ? days + 'd ' : ''}
    ${hours || days ? hours + 'h ' : ''}
    ${minutes || hours ? minutes + 'm ' : ''}
    ${!hours && (seconds || minutes) ? seconds + 's ' : ''}`;
  };

  const renderLabel = () => {
    if (pendingLaunch) {
      return getCountDown();
    } else if (rewardProgramExpired) {
      return 'Entry Closed';
    } else {
      return 'Subscribe';
    }
  };

  const wrappableTokenAddresses = wrappableTokens!.flatMap((token) => [
    token.wrapperTokenAddress.toLowerCase()
  ]);

  const [
    matchingUnderlyingTokenAddress,
    matchingUnderlyingTokenSymbol,
    matchingWrapperTokenSymbol
  ] = useMemo(() => {
    const match = wrappableTokens!.find(
      (token) =>
        token.wrapperTokenAddress.toLowerCase() ===
        stakingToken.address.toLowerCase()
    );
    if (match) {
      return [
        match.underlyingTokenAddress,
        match.underlyingTokenSymbol,
        match.wrapperTokenSymbol
      ];
    } else {
      return [null, null, null];
    }
  }, [wrappableTokens, stakingToken.address]);

  return (
    <>
      <Text fontSize='lg' fontWeight='600' mb={4} textAlign='left'>
        Actions
      </Text>
      <SimpleGrid columns={[1, null, 2]} gap={10} textAlign='left'>
        <Box bg='gray.700' borderRadius='xl' p={5}>
          {stakingToken && (
            <>
              <HStack alignItems='stretch'>
                <Text color='gray.300' mb={2}>
                  Available token balance
                </Text>
                {stakingTokenData ? (
                  <Text fontWeight='600' textAlign='right' flexGrow={1}>
                    {formatNumber.token(
                      stakingTokenData.stakingTokenUnlocked.add(
                        stakingTokenData.stakingTokenInWallet
                      ),
                      stakingTokenData.stakingTokenDecimals
                    )}
                  </Text>
                ) : (
                  <Box textAlign='right' flexGrow={1}>
                    <Spinner size='sm' />
                  </Box>
                )}
              </HStack>
              <SimpleGrid
                columns={2}
                bg='gray.600'
                borderRadius='lg'
                alignItems='center'
              >
                <Text textAlign='center' fontWeight='600' isTruncated px={4}>
                  {stakingToken.tokenSymbol}
                </Text>
                <Button
                  _focus={{
                    boxShadow: '0 0 0 3px #2B3350'
                  }}
                  borderRadius='lg'
                  disabled={
                    blockTimestampLoading ||
                    subscriptionsLoading ||
                    rewardProgramExpired ||
                    pendingLaunch ||
                    !extendedProgram ||
                    !stakingTokenData ||
                    (stakingTokenData &&
                      stakingTokenData.stakingTokenUnlocked
                        .add(stakingTokenData.stakingTokenInWallet)
                        .eq(0))
                  }
                  isLoading={
                    !stakingTokenData ||
                    blockTimestampLoading ||
                    subscriptionsLoading ||
                    rewardProgramStateLoading
                  }
                  onClick={() =>
                    openModal(ModalType.addSubscription, {
                      crucible,
                      stakingToken: stakingToken as ProgramToken,
                      stakingTokenCrucibleBalance:
                        stakingTokenData!.stakingTokenUnlocked,
                      stakingTokenWalletBalance:
                        stakingTokenData!.stakingTokenInWallet,
                      stakingTokenDecimals:
                        stakingTokenData!.stakingTokenDecimals,
                      currentRewardProgram: currentRewardProgram,
                      rewardProgramTokens: extendedProgram!.rewardProgramTokens
                    })
                  }
                >
                  {renderLabel()}
                </Button>
              </SimpleGrid>
              {wrappableTokenAddresses.includes(
                stakingToken.address.toLowerCase()
              ) && (
                <Text fontSize='small' mt={2}>
                  <ChakraLink
                    isExternal
                    color='cyan.400'
                    href={currentRewardProgram.getStakingTokenUrl}
                  >
                    {matchingUnderlyingTokenSymbol}
                  </ChakraLink>
                  {' needs to be '}
                  <ChakraLink
                    as='button'
                    color='cyan.400'
                    onClick={() => {
                      setSelectedWalletAsset(
                        (walletAssets || []).find(
                          (asset: any) =>
                            asset.address.toLowerCase() ===
                            matchingUnderlyingTokenAddress?.toLowerCase()
                        ) || null
                      );
                      setTransferTabIndex(3);
                      setTabIndex(1);
                    }}
                  >
                    wrapped into
                  </ChakraLink>
                  {` ${matchingWrapperTokenSymbol}`} before staking
                </Text>
              )}
            </>
          )}
        </Box>
        <Box bg='gray.700' borderRadius='xl' p={5}>
          {stakingToken && (
            <>
              <HStack alignItems='stretch'>
                <Text color='gray.300' mb={2}>
                  Subscribed token balance
                </Text>
                {stakingTokenData ? (
                  <Text fontWeight='600' textAlign='right' flexGrow={1}>
                    {formatNumber.token(
                      stakingTokenData.stakingTokenLocked,
                      stakingToken.decimals
                    )}
                  </Text>
                ) : (
                  <Box textAlign='right' flexGrow={1}>
                    <Spinner size='sm' />
                  </Box>
                )}
              </HStack>
              <SimpleGrid
                columns={2}
                bg='gray.600'
                borderRadius='lg'
                alignItems='center'
              >
                <Text textAlign='center' fontWeight='600' isTruncated px={4}>
                  {stakingToken.tokenSymbol}
                </Text>
                <Button
                  borderRadius='lg'
                  disabled={
                    isExiting ||
                    subscriptionsLoading ||
                    !stakingTokenData ||
                    (stakingTokenData &&
                      stakingTokenData.stakingTokenLocked.eq(0))
                  }
                  isLoading={
                    !stakingTokenData ||
                    subscriptionsLoading ||
                    rewardProgramStateLoading ||
                    isExiting
                  }
                  onClick={() =>
                    rewardProgramState === PowerSwitchStatus.OFFLINE ||
                    rewardProgramState === PowerSwitchStatus.SHUTDOWN
                      ? openModal(ModalType.rageQuit, {
                          type: rewardProgramState,
                          crucibleId: crucible.id,
                          currentRewardProgram,
                          stakingTokenAddress: stakingToken.address
                        })
                      : openModal(ModalType.claimRewards, {
                          crucibleId: crucible.id,
                          subscriptionIdx: 0,
                          stakingTokenSymbol: stakingToken.tokenSymbol
                        })
                  }
                >
                  {rewardProgramState === PowerSwitchStatus.OFFLINE ||
                  rewardProgramState === PowerSwitchStatus.SHUTDOWN
                    ? 'Forced Exit'
                    : 'Unsubscribe'}
                </Button>
              </SimpleGrid>
            </>
          )}
        </Box>
      </SimpleGrid>
    </>
  );
};

export default RewardProgramActions;
